import React, { useState, useEffect, useCallback } from "react";
import { MdAdd, MdClose, MdAutoAwesome } from "react-icons/md";
import Button, { IconButton } from "components/button";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState } from "components/states";
import { Checkbox, Select, Textarea } from "components/fields";
import { useConfirm } from "components/dialog/ConfirmProvider";
import teamService from "services/teamService";
import { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { QUESTION_TYPES, questionTypeLabel } from "./teamDisplay";

// Leaders add open questions here; multiple choice is set up on the Check-Ins page.
const NEW_QUESTION_TYPES = QUESTION_TYPES.filter((t) => t.value !== "select");

const SCOPE_OPTIONS = [
  { value: "direct_reports", label: "Direct Reports" },
  { value: "subtree", label: "Whole Team Below" },
];

/**
 * Inline manager for one leader's check-in questions, shown in the org
 * chart drawer. Lets admins (and leaders above them) set up what this
 * leader asks their team, including AI-suggested questions.
 */
const LeaderQuestionsPanel = ({ leader, onChanged }) => {
  const confirm = useConfirm();
  const [questions, setQuestions] = useState(null);
  const [newPrompt, setNewPrompt] = useState("");
  const [newType, setNewType] = useState("text");
  const [newScope, setNewScope] = useState("direct_reports");
  const [adding, setAdding] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [savingPicked, setSavingPicked] = useState(false);

  const fetchQuestions = useCallback(async () => {
    try {
      const res = await teamService.getQuestions({ leaderId: leader.id });
      setQuestions(res.data || []);
    } catch (error) {
      setQuestions([]);
    }
  }, [leader.id]);

  useEffect(() => {
    setQuestions(null);
    setSuggestions(null);
    setNewPrompt("");
    fetchQuestions();
  }, [fetchQuestions]);

  const addQuestion = async () => {
    if (!newPrompt.trim()) return;
    try {
      setAdding(true);
      await teamService.createQuestion({
        leaderId: leader.id,
        prompt: newPrompt.trim(),
        questionType: newType,
        appliesTo: newScope,
      });
      setNewPrompt("");
      notify.success("Question added");
      fetchQuestions();
      onChanged && onChanged();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not add question"));
    } finally {
      setAdding(false);
    }
  };

  const removeQuestion = async (q) => {
    const confirmed = await confirm({
      title: `Delete "${q.prompt}"?`,
      message: `${leader.firstName}'s team will stop seeing this question.`,
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await teamService.deleteQuestion(q.id);
      fetchQuestions();
      onChanged && onChanged();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not delete question"));
    }
  };

  const generate = async () => {
    try {
      setSuggesting(true);
      setSuggestions(null);
      const res = await teamService.suggestQuestions({ leaderId: leader.id });
      const items = res.data || [];
      setSuggestions(items);
      setPicked(new Set(items.map((_, i) => i)));
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Couldn't generate suggestions"));
    } finally {
      setSuggesting(false);
    }
  };

  const togglePicked = (i) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const addPicked = async () => {
    const chosen = (suggestions || []).filter((_, i) => picked.has(i));
    if (!chosen.length) return;
    try {
      setSavingPicked(true);
      for (const s of chosen) {
        await teamService.createQuestion({
          leaderId: leader.id,
          prompt: s.prompt,
          questionType: s.questionType,
          options: s.options,
          appliesTo: "direct_reports",
        });
      }
      notify.success(`${chosen.length} question${chosen.length === 1 ? "" : "s"} added`);
      setSuggestions(null);
      fetchQuestions();
      onChanged && onChanged();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not add some questions"));
      fetchQuestions();
    } finally {
      setSavingPicked(false);
    }
  };

  return (
    <section className="mt-6" aria-labelledby="leader-questions-heading">
      <div className="flex items-center justify-between gap-2">
        <h3 id="leader-questions-heading" className="text-sm font-bold text-fg">
          Questions {leader.firstName} Asks Their Team
        </h3>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<MdAutoAwesome className="h-4 w-4" />}
          loading={suggesting}
          onClick={generate}
        >
          Suggest with AI
        </Button>
      </div>

      {questions === null ? (
        <Skeleton lines={2} className="mt-3" />
      ) : questions.length === 0 && !suggestions ? (
        <EmptyState
          variant="inline"
          title="Nothing yet. Add a question below or let AI draft some based on this team."
        />
      ) : (
        <ul className="mt-2 flex flex-col gap-1.5">
          {questions.map((q) => (
            <li
              key={q.id}
              className="flex items-start justify-between gap-2 rounded-control bg-surface-raised px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-fg">{q.prompt}</p>
                <p className="text-xs text-fg-muted">
                  {questionTypeLabel(q.questionType)}
                  {q.appliesTo === "subtree" ? " · cascades to whole team" : ""}
                </p>
              </div>
              <IconButton
                icon={<MdClose className="h-4 w-4" />}
                label="Delete question"
                size="sm"
                onClick={() => removeQuestion(q)}
              />
            </li>
          ))}
        </ul>
      )}

      {suggestions && (
        <fieldset className="mt-3 rounded-control border border-line bg-surface-raised p-3">
          <legend className="px-1 text-xs font-semibold text-fg">
            Suggested for this team. Uncheck any you don't want.
          </legend>
          <div className="mt-1 flex flex-col gap-2">
            {suggestions.map((s, i) => (
              <Checkbox
                key={i}
                label={s.prompt}
                description={`${questionTypeLabel(s.questionType)}${s.reason ? ` · ${s.reason}` : ""}`}
                checked={picked.has(i)}
                onChange={() => togglePicked(i)}
              />
            ))}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setSuggestions(null)}>
              Dismiss
            </Button>
            <Button
              size="sm"
              loading={savingPicked}
              disabled={picked.size === 0}
              onClick={addPicked}
            >
              Add {picked.size} Selected
            </Button>
          </div>
        </fieldset>
      )}

      <form
        className="mt-3 flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          addQuestion();
        }}
      >
        <Textarea
          label="New Question"
          rows={2}
          value={newPrompt}
          onChange={(e) => setNewPrompt(e.target.value)}
          placeholder={`Ask ${leader.firstName}'s team something…`}
        />
        <div className="flex flex-wrap items-end gap-2">
          <Select
            aria-label="Answer type"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            options={NEW_QUESTION_TYPES}
            className="w-auto min-w-[140px] flex-1"
          />
          <Select
            aria-label="Asked of"
            value={newScope}
            onChange={(e) => setNewScope(e.target.value)}
            options={SCOPE_OPTIONS}
            className="w-auto min-w-[140px] flex-1"
          />
          <Button
            type="submit"
            leftIcon={<MdAdd className="h-4 w-4" />}
            loading={adding}
            disabled={!newPrompt.trim()}
          >
            Add Question
          </Button>
        </div>
      </form>
    </section>
  );
};

export default LeaderQuestionsPanel;
